import React, { useState } from "react";
import { Modal, Button, Input, Label, Textarea } from "@/components/ui";
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import UpsellModal from "../../modals/UpsellModal";
import { useUIStore } from "../../../store/uiStore";

interface CreateHelpCenterForm {
  title: string;
  description: string;
  slug: string;
}

interface CreateHelpCenterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (form: CreateHelpCenterForm) => Promise<void>;
  isCreating: boolean;
  error: string | null;
}

export const CreateHelpCenterModal: React.FC<CreateHelpCenterModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isCreating,
  error,
}) => {
  const { t } = useTranslation();
  const { openModal, closeModal, modals } = useUIStore();
  const [form, setForm] = useState<CreateHelpCenterForm>({
    title: "",
    description: "",
    slug: "",
  });
  const [formError, setFormError] = useState<string | null>(error);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Sync external error prop
  React.useEffect(() => {
    setFormError(error);
    // If error mentions limit or upgrade, show upsell modal
    if (error && (error.toLowerCase().includes('limit') || error.toLowerCase().includes('upgrade'))) {
      openModal('upsell');
    }
  }, [error, openModal]);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setForm({ title: "", description: "", slug: "" });
      setSlugManuallyEdited(false);
      setFormError(null);
    }
  }, [isOpen]);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (title: string) => {
    setForm({
      ...form,
      title,
      // Only auto-generate slug if user hasn't manually edited it
      slug: slugManuallyEdited ? form.slug : generateSlug(title),
    });
    // Clear error when user starts typing
    if (formError) {
      setFormError(null);
    }
  };

  const handleSlugChange = (slug: string) => {
    setForm({ ...form, slug });
    // Mark slug as manually edited when user types in it
    setSlugManuallyEdited(true);
    // Clear error when user starts typing
    if (formError) {
      setFormError(null);
    }
  };

  const handleClose = () => {
    setForm({ title: "", description: "", slug: "" });
    setFormError(null);
    onClose();
  };

  const handleSubmit = async () => {
    await onSubmit(form);
  };

  return (
    <>
      <UpsellModal
        isOpen={modals.upsell}
        onClose={() => closeModal("upsell")}
      />
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('helpCenter.createHelpCenterModal.title')}
        size="md"
      >
        <div className="space-y-4 p-4">
          {/* Error Display */}
          {formError && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
              <p className="text-sm text-destructive">{formError}</p>
            </div>
          )}

        <div>
          <Label htmlFor="title">{t('helpCenter.createHelpCenterModal.titleLabel')}</Label>
          <Input
            id="title"
            value={form.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder={t('helpCenter.createHelpCenterModal.titlePlaceholder')}
            className={`mt-1 ${formError ? 'border-destructive' : ''}`}
          />
        </div>
        <div>
          <Label htmlFor="slug">{t('helpCenter.createHelpCenterModal.slug')}</Label>
          <Input
            id="slug"
            value={form.slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder={t('helpCenter.createHelpCenterModal.slugPlaceholder')}
            className={`mt-1 ${formError && formError.includes('Slug') ? 'border-destructive' : ''}`}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {t('helpCenter.createHelpCenterModal.slugHint')}
          </p>
        </div>
        <div>
          <Label htmlFor="description">{t('helpCenter.createHelpCenterModal.description')}</Label>
          <Textarea
            id="description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder={t('helpCenter.createHelpCenterModal.descriptionPlaceholder')}
            className="mt-1"
            rows={3}
          />
        </div>
      </div>
      <div className="flex justify-end space-x-3 p-4 border-t border-gray-200">
        <Button
          variant="outline"
          onClick={handleClose}
          disabled={isCreating}
          style={{ backgroundColor: '#efefef' }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isCreating}
          loading={isCreating}
        >
          {t('common.create')}
        </Button>
      </div>
    </Modal>
    </>
  );
};

